import { callDeleteAuthFederationAdminRequest } from '../requests/DeleteAuthFederationAdminRequest';
import { callDeleteAuthFederationConnectorRequest } from '../requests/DeleteAuthFederationConnectorRequest';
import { callDeleteAuthFederationKeyRequest } from '../requests/DeleteAuthFederationKeyRequest';
import { callGetAuthFederationAdminRequest } from '../requests/GetAuthFederationAdminRequest';
import { callGetAuthFederationConnectorRequest } from '../requests/GetAuthFederationConnectorRequest';
import { callGetAuthFederationConnectorsRequest } from '../requests/GetAuthFederationConnectorsRequest';
import { callGetAuthFederationKeysRequest } from '../requests/GetAuthFederationKeysRequest';
import { callPatchAuthFederationAdminRequest } from '../requests/PatchAuthFederationAdminRequest';
import { callPostAuthFederationConnectorRequest } from '../requests/PostAuthFederationConnectorRequest';
import { callPostAuthFederationKeyRequest } from '../requests/PostAuthFederationKeyRequest';
import { callPutAuthFederationAdminRequest } from '../requests/PutAuthFederationAdminRequest';
import { callPutAuthFederationKeyActivationRequest } from '../requests/PutAuthFederationKeyActivationRequest';

export interface IAuthFederationAdmin {
  enabled: boolean;
  tokenLifetimeSeconds: number;
  issuerUrl: string;
  activeKeyId: string | null;
}

export interface IGetAuthFederationAdminResponse {
  authFederationAdmin: IAuthFederationAdmin;
}

export interface IAuthFederationEnableRequestBody {
  enabled: boolean;
  tokenLifetimeSeconds?: number;
}

export interface IAuthFederationAdminRequestBody {
  tokenLifetimeSeconds?: number;
}

export interface IAuthFederationKey {
  kid: string;
  alg: string;
}

export interface IGetAuthFederationKeysResponse {
  keys: IAuthFederationKey[];
  activeKid: string | null;
}

export interface IAuthFederationConnector {
  luid: string;
  connectorClass: string;
  audience: string;
}

export namespace AuthFederationService {

  export function getAdmin(): Promise<IAuthFederationAdmin> {
    return callGetAuthFederationAdminRequest().then((r) => r.authFederationAdmin);
  }

  export function initialEnable(): Promise<IAuthFederationAdmin> {
    return callPutAuthFederationAdminRequest({ enabled: true }).then((r) => r.authFederationAdmin);
  }

  export function patch(body: IAuthFederationAdminRequestBody): Promise<IAuthFederationAdmin> {
    return callPatchAuthFederationAdminRequest(body).then((r) => r.authFederationAdmin);
  }

  export function getKeys(): Promise<IAuthFederationKey[]> {
    return callGetAuthFederationKeysRequest().then((r) => r.keys);
  }

  export function createKey(): Promise<IAuthFederationKey> {
    return callPostAuthFederationKeyRequest();
  }

  export function activateKey(kid: string): Promise<void> {
    return callPutAuthFederationKeyActivationRequest(kid, 'activate');
  }

  export function deactivateKey(kid: string): Promise<void> {
    return callPutAuthFederationKeyActivationRequest(kid, 'deactivate');
  }

  export function deleteKey(kid: string): Promise<void> {
    return callDeleteAuthFederationKeyRequest(kid);
  }

  export function deleteAdmin(): Promise<void> {
    return callDeleteAuthFederationAdminRequest();
  }

  // ---- Connector Management ----

  export function createConnector(
    connectorClass: string,
    audience: string
  ): Promise<IAuthFederationConnector> {
    return callPostAuthFederationConnectorRequest({ connectorClass, audience }).then(
      (r) => r.connector
    );
  }

  export function listConnectors(): Promise<IAuthFederationConnector[]> {
    return callGetAuthFederationConnectorsRequest().then((r) => r.connectors);
  }

  export function getConnector(luid: string): Promise<IAuthFederationConnector> {
    return callGetAuthFederationConnectorRequest(luid).then((r) => r.connector);
  }

  export function deleteConnector(luid: string): Promise<void> {
    return callDeleteAuthFederationConnectorRequest(luid);
  }
}
